import React, { useEffect, useRef, useState } from 'react';
import Wrapper from './Wrapper';
import Tips from '../../../components/Request/Tips';
import UploadFile from '../../../components/Request/UploadFile';
import './Finishes.scss';
import { useDispatch, useSelector } from 'react-redux';
import {
  requestsSelector,
  setCurrentRequestFilesSize,
  setNextButtonEnabled,
  setNextButtonToolTip,
  setRequestData,
  updateRequestPages,
} from '../../../redux/slicers/requestSlicer';
import { Loader, StyledSelect } from '../../../common';
import icons from '../../../assets';
import { checkFileTypes, checkIsHexColor, getNameFromUrl, uploadRequestFile } from './helper';
import { errorToast, successToast } from '../../../helpers/toastHelper';
import {
  apiDeleteRequestPart,
  apiDeleteRequestTexture,
  apiGetRequestParts,
  apiGetRequestTexture,
  apiPostImageOnPresignedData,
  apiUpdateRequestPart,
  apiUpdateRequestTexture,
  apiUploadRequestParts,
  apiUploadRequestTexture,
} from '../../../helpers/api';
import { getAppState } from '../../../redux/store';
import cx from 'classnames';
import UploadedFiles from '../../../components/Request/UploadedFiles';
import { ElipsisTooltip } from '../../../common/ElipsisTooltip';
import { ACCEPTABLE_IMAGE_TYPE_REQUEST } from "../../../constants/constants";
import { removeExifFromMultipleFiles } from '../../../helpers/jsHelper';

// Helper function to resize image for thumbnail (512x512)
const resizeImageForThumbnail = (file, maxWidth = 512, maxHeight = 512) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let width = img.width;
      let height = img.height;

      // Calculate the new dimensions while maintaining aspect ratio
      if (width > height) {
        if (width > maxWidth) {
          height = Math.round((height * maxWidth) / width);
          width = maxWidth;
        }
      } else {
        if (height > maxHeight) {
          width = Math.round((width * maxHeight) / height);
          height = maxHeight;
        }
      }

      canvas.width = width;
      canvas.height = height;

      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0, width, height);

      canvas.toBlob(
        (blob) => {
          const resizedFile = new File([blob], file.name, {
            type: file.type || 'image/jpeg',
            lastModified: Date.now(),
          });
          resolve(resizedFile);
        },
        file.type || 'image/jpeg',
        0.85
      );
    };
    img.src = URL.createObjectURL(file);
  });
};

let imageURL = null,
  actualId = null;
function Finishes({ className, isReadOnly }) {
  const _requestSelector = useSelector(requestsSelector);
  const [parts, setParts] = useState();

  const [value, setValue] = useState({});
  const [copyFrom, setCopyFrom] = useState({});
  const [images, setImages] = useState({});
  const [colourReference, setColourReference] = useState({});
  const [showOption, setShowOption] = useState();
  const [showPartMenu, setShowPartMenu] = useState();
  const [showColorInput, setShowColorInput] = useState();
  const [renamePart, setRenamePart] = useState(null);
  const [rename, setRename] = useState();
  const [loading, setLoading] = useState(false);
  const [linkedTextures, setLinkedTextures] = useState([]);
  const [defaultFlag, setDefaultFlag] = useState(false);

  const copyFromRef = useRef();
  const valueRef = useRef();
  const dispatch = useDispatch();

  const { RequestThreeDotIcon } = icons;

  const TIP_TEXT =
    'Please provide the digital swatches you prefer as the default finish for your product. \n\nThe swatch must have a minimum resolution of 1500 x 1500 pixels.';

  useEffect(() => {
    if (defaultFlag) {
      const file = images?.[actualId || '0'][images?.[actualId || '0']?.length - 1];
      setDefaultFlag(false)
      changeColorReference(
        {
          value: file,
          oldColorCode: undefined,
        },
        actualId || 0
      );
    }
  }, [images]);

  const handleChange = async (e, _id) => {
    setValue({ ...value, ...{ [_id]: e } });
    valueRef.current = { ...value, ...{ [_id]: e } };

    setCopyFrom({ ...copyFrom, ...{ [_id]: e.id ? e.id : false } });
    copyFromRef.current = { ...copyFrom, ...{ [_id]: e.id ? e.id : false } };

    if (linkedTextures.length) {
      linkedTextures.forEach(async (_item) => {
        if (_item?.model_request_part === _id) await apiDeleteRequestTexture(_item.id);
      });

      setLinkedTextures(linkedTextures.filter((_item) => _item?.model_request_part !== _id));
    }
  };

  const handleFileChange = async (files, title, _id, isDefault) => {
    const formData = new FormData();

    formData.append('three_d_model_request', _requestSelector.currentRequestId);
    +_id !== 0 && formData.append('model_request_part', _id);

    if (isDefault) {
      formData.append('is_default', true);
    } else {
      +_id !== 0 && formData.append('model_request_part', _id);
    }

    files.forEach((item) => {
      formData.append('file_name', item?.name);
      formData.append('file_size', item?.size);
    });

    const updatedData = await uploadRequestFile(
      formData,
      files,
      null,
      null,
      apiUploadRequestTexture,
      false,
      dispatch,
      _requestSelector
    );

    // Upload both original file and thumbnail to S3 using presigned URLs
    await Promise.all(
      updatedData.map(async (_file) => {
        const uploadPromises = [];

        // Upload original file
        if (_file.presigned_data_file) {
          uploadPromises.push(apiPostImageOnPresignedData(_file.file, _file.presigned_data_file));
        }

        // Upload thumbnail (resized to 512x512)
        if (_file.presigned_data_thumbnail) {
          const thumbnailFile = await resizeImageForThumbnail(_file.file, 512, 512);
          uploadPromises.push(apiPostImageOnPresignedData(thumbnailFile, _file.presigned_data_thumbnail));
        }

        return Promise.all(uploadPromises);
      })
    );

    setImages({
      ...images,
      ...{ [_id]: [...(images[_id]?.length ? images[_id] : []), ...updatedData] },
    });
  };

  const handleImageRemove = async (image_id, _id) => {
    const _file = images[_id].find((item) => item.id === image_id);
    if (_file?.size)
      dispatch(
        setCurrentRequestFilesSize(_requestSelector.currentRequestFilesSize - _file.file_size)
      );

    const _images = images[_id].filter((_image) => _image.id !== image_id);
    const final_images = {
      ...images,
      ...{
        [_id]: _images,
      },
    };

    if (!_images?.length) delete final_images[_id];

    setImages(final_images);

    await apiDeleteRequestTexture(image_id);
  };

  const changeColorReference = async (_data, _id) => {
    setLoading(true);
    const formData = new FormData();
    let _colorReference;
    let deletedImageId;

    if (_id === 0) formData.append('is_default', true);

    formData.append('three_d_model_request', _requestSelector.currentRequestId);
    
    +_id !== 0 && formData.append('model_request_part', _id);

    if (colourReference[_id]?.value?.id) {
      const _formData = new FormData();
      _formData.append('three_d_model_request', _requestSelector.currentRequestId);
      _formData.append('is_color_reference', false);
      if (_id === 0) _formData.append('is_default', true);
      await apiUpdateRequestTexture(colourReference[_id]?.value?.id, _formData);
    }

    if (_data?.isColorCode) {
      formData.append('color_code', _data?.value?.color_code);
      formData.append('is_color_reference', true);
      +_id !== 0 && formData.append('model_request_part', _id);

      if (_data?.oldColorCode?.id) {
        _colorReference = await apiUpdateRequestTexture(_data?.oldColorCode?.id, formData);
      } else {
        _colorReference = await apiUploadRequestTexture(formData);
      }
    } else {
      if (_data?.oldColorCode?.id) {
        await apiDeleteRequestTexture(_data?.oldColorCode?.id);
        deletedImageId = _data?.oldColorCode?.id;
      }

      formData.append('is_color_reference', true);
      await apiUpdateRequestTexture(_data.value.id, formData);
    }

    if (!_colorReference?.status) _colorReference = _data;
    else _colorReference = { value: _colorReference.data[0] || _colorReference.data };

    setColourReference({
      ...colourReference,
      ...{ [_id]: _colorReference },
    });

    setImages({
      ...images,
      [_id]: images[_id]
        .map((_item) => {
          if (_item.id === _data.value.id) return { ..._item, is_color_reference: true };
          return { ..._item, is_color_reference: false };
        })
        .filter((_item) => _item.id !== deletedImageId),
    });

    setLoading(false);
    imageURL = null;
    actualId = null;
  };

  const handleColourReferenceChange = (e, _id, isDefault = false) => {
    e.preventDefault();
    const src = e.dataTransfer.getData('text/plain');
    if (!images[_id].filter((item) => item?.url === src).length) return;

    changeColorReference(
      {
        value: images[_id].find((item) => item?.url === src),
        oldColorCode: images[_id].find((item) => item?.color_code),
        isDefault,
      },
      _id
    );
  };

  const handleColourReferenceRemove = async (e, _id) => {
    if (e) e.stopPropagation();
    const _colourReference = colourReference;
    setLoading(true);
    if (_colourReference[_id]?.value)
      if (!_colourReference[_id]?.value?.color_code) {
        const formData = new FormData();
        formData.append('is_color_reference', false);
        if (_id === 0) {
          formData.append('is_default', true);
        }
        await apiUpdateRequestTexture(_colourReference[_id].value.id, formData);
        setImages({
          ...images,
          [_id]: images[_id].map((_item) => ({ ..._item, is_color_reference: false })),
        });
      } else {
        await apiDeleteRequestTexture(_colourReference[_id]?.value?.id);
        setImages({
          ...images,
          [_id]: images[_id].filter((_item) => _item.id !== _colourReference[_id]?.value?.id),
        });
      }

    delete _colourReference[_id];
    setLoading(false);
    setColourReference(_colourReference);
    setShowOption(null);
  };

  const handleInputBlur = (_id, _value, isDefault = false) => {
    setShowColorInput(null);
    setShowOption(null);

    let value = _value;

    if (!_value) return;

    if (!checkIsHexColor(_value)) {
      if (!checkIsHexColor('#' + _value)) {
        errorToast('Entered hex color is not valid.');
        return;
      }

      value = '#' + _value;
    }

    if (!_value) {
      handleColourReferenceRemove('', _id);
      return;
    }

    changeColorReference(
      {
        value: { color_code: value },
        isColorCode: true,
        isDefault,
        oldColorCode: images[_id].find((item) => item?.color_code),
      },
      _id
    );
  };

  const handleKeyDown = (e) => {
    if (e.keyCode === 13) e.target.blur();
  };

  const handleEnterPartName = (e) => {
    e.stopPropagation();
  };

  const handlePartNameKeyDown = (_id, e) => {
    if (e.keyCode === 13) {
      e.target.blur();
    }
  };

  const handleEnterPartNameBlur = async (_id, e) => {
    const _value = e.target.value;
    if (!_value) return;
    const _part = parts.filter((item) => item?.id === _id)[0];

    let response;

    const paylod = JSON.stringify([
      {
        three_d_model_request: _requestSelector.currentRequestId,
        requested_model_file: _part?.currentImageId,
        name: _value,
        sequence_number: _part.id,
        json_data: { name: _value, label: _value, value: _value },
      },
    ]);

    if (!_part?.actual_id) {
      response = await apiUploadRequestParts(paylod);

      if (response.status) {
        const _data = response?.data[0];
        _part.actual_id = _data?.id;
      }
    } else {
      response = await apiUpdateRequestPart(_part?.actual_id, paylod);
    }
    _part.name = _value;
    _part.value = _value;

    setParts(
      parts.map((item) => {
        if (item?.id === _id) return _part;
        else return item;
      })
    );
  };

  const handleRenameKeyDown = (e) => {
    if (e.keyCode === 13) {
      e.target.blur();
    }
  };

  const handleRenameBlur = async (_id) => {
    if (!rename) return;
    const _part = parts.map((_part) => {
      if (_part.actual_id === _id) {
        _part.name = rename;
        _part.value = rename;
        _part.label = rename;
      }
      return _part;
    });

    const _updatedPart = _part.find((_item) => _item.actual_id === _id);

    const payload = JSON.stringify({
      name: rename,
      json_data: _updatedPart,
    });

    await apiUpdateRequestPart(_id, payload);

    setParts(_part);
    setRenamePart(null);
    setRename('');
    setShowPartMenu(null);
  };

  const handleRenameChange = (e) => {
    const _value = e.target.value;
    setRename(_value);
  };

  const handleRename = (e, _id, _value) => {
    e.preventDefault();
    e.stopPropagation();
    setRename(_value);
    setRenamePart(_id);
  };

  const handleDuplicate = () => {};

  const handlePartDelete = async (_id) => {
    setLoading(true);
    await apiDeleteRequestPart(_id);

    if (!parts.filter((_part) => _part.actual_id !== _id).length) {
      setParts([
        {
          id: 0,
          value: 'Default Finish images',
          name: 'Default Finish images',
          isDefault: true,
          actual_id: 0,
        },
      ]);
      const _pageData = getAppState().requests.requestPages.find((_page) => _page.id === 5);
      dispatch(updateRequestPages({ ..._pageData, data: { images: [], parts: [] } }));
    } else setParts(parts.filter((_part) => _part.actual_id !== _id));
    setLoading(false);
  };

  useEffect(() => {
    if (isReadOnly) return;
    dispatch(setNextButtonEnabled(false));
    window.addEventListener('click', () => {
      setShowOption(null);
      setShowColorInput(null);
      setShowPartMenu(null);
      setRenamePart(null);
      setRename('');
    });

    return async () => {
      setNextButtonToolTip(null);
      window.removeEventListener('click', () => {
        setShowOption(null);
        setShowColorInput(null);
        setShowPartMenu(null);
        setRenamePart(null);
        setRename('');
      });

      if (copyFromRef.current && Object.keys(copyFromRef.current)?.length) {
        Object.keys(copyFromRef.current).forEach(async (_item) => {
          if (
            valueRef.current?.[_item]?.actual_id &&
            !linkedTextures.filter((__item) => __item.model_request_part === parseInt(_item)).length
          ) {
            const formData = new FormData();
            formData.append('is_link', true);
            formData.append('parent_texture', valueRef.current[_item]?.actual_id);
            formData.append('model_request_part', _item);
            await apiUploadRequestTexture(formData);
          }
        });
      }
    };
  }, []);

  useEffect(async () => {
    let _images = {};
    let _parts;
    let _copyFrom = {};
    let _value = {};
    let _linkedTexture = [];
    setLoading(true);

    if (!_requestSelector.hasCustomizableparts) {
      setParts([
        {
          id: 0,
          value: 'Default Finish images',
          name: 'Default Finish images',
          isDefault: true,
          actual_id: 0,
        },
      ]);

      const seatingTextures = await apiGetRequestTexture(
        `?is_default=true&three_d_model_request=${_requestSelector.currentRequestId}&per_page=100`
      );

      if (seatingTextures?.results?.length) {
        const _images = seatingTextures?.results?.map((_im) => ({
          ..._im,
          url: _im.image,
          file: { name: getNameFromUrl(_im.image), size: null },
        }));

        setImages({
          ...images,
          ...{ 0: _images?.filter((_item) => _item.image || _item.is_color_reference) },
        });
      }
      setLoading(false);
      return;
    }

    const partsResponse = await apiGetRequestParts(
      `?three_d_model_request=${_requestSelector.currentRequestId}&per_page=100`
    );

    if (partsResponse?.results?.length) {
      _parts = partsResponse?.results?.map((_item) => {
        if (_item.json_data) {
          return { ..._item.json_data, actual_id: _item.id };
        } else return _item;
      });

      await Promise.all(
        _parts?.map(async (_part) => {
          if (_part?.actual_id) {
            const seatingTextures = await apiGetRequestTexture(
              '?per_page=100&model_request_part=' + _part?.actual_id
            );

            _images = {
              ..._images,
              [_part?.actual_id]: seatingTextures?.results
                ?.map((_im) => ({
                  ..._im,
                  url: _im.image,
                  file: { name: getNameFromUrl(_im.image), size: null },
                }))
                .filter((_item) => !_item.is_link),
            };

            _linkedTexture = [
              ..._linkedTexture,
              ...seatingTextures?.results.filter((_item) => _item.is_link),
            ];

            seatingTextures?.results?.forEach((_item) => {
              if (_item?.parent_texture && _item?.is_link) {
                _copyFrom = { ..._copyFrom, [_part.actual_id]: _item.parent_texture };
                _value = {
                  ..._value,
                  [_part.actual_id]: _parts.find(
                    (__part) => __part.actual_id === _item.parent_texture
                  ),
                };
                return;
              }
            });
          }
        })
      );

      if (_copyFrom) setCopyFrom(_copyFrom);
      if (_value) setValue(_value);
      setParts(_parts);
      if (_images) setImages(_images);
      setLinkedTextures(_linkedTexture);
    } else {
      setParts([
        {
          id: 0,
          value: 'Default Finish images',
          name: 'Default Finish images',
          isDefault: true,
          actual_id: 0,
        },
      ]);

      const seatingTextures = await apiGetRequestTexture(
        `?is_default=true&three_d_model_request=${_requestSelector.currentRequestId}&per_page=100`
      );

      if (seatingTextures?.results?.length) {
        const _images = seatingTextures?.results?.map((_im) => ({
          ..._im,
          url: _im.image,
          file: { name: getNameFromUrl(_im.image), size: null },
        }));

        setImages({
          ...images,
          ...{ 0: _images?.filter((_item) => _item.image || _item.is_color_reference) },
        });
      }
      setLoading(false);
    }

    setLoading(false);
  }, []);

  useEffect(() => {
    let _colourReference = {};

    Object.keys(images).forEach((_image) => {
      const _tempColourReference = images[_image]?.find((_item) => _item?.is_color_reference);

      if (_tempColourReference) {
        _colourReference[_image] = { value: _tempColourReference };
      }
    });

    if (Object.keys(_colourReference).length)
      setColourReference({ ...colourReference, ..._colourReference });
  }, [images, copyFrom, value]);

  useEffect(() => {
    if (parts?.length && Object.keys(images)?.length) {
      const _images = Object.keys(images).filter((image) => images[image]?.length);
      const _copyFrom = Object.keys(copyFrom).filter((_item) => copyFrom[_item]);
      const _colourReference = Object.keys(colourReference).map(
        (c) => colourReference[c]?.value?.id
      );

      if (
        _images.length + _copyFrom.length === parts.length &&
        _images.length === _colourReference.length
      ) {
        dispatch(setNextButtonEnabled(true));
        dispatch(setNextButtonToolTip(null));
      } else {
        dispatch(setNextButtonEnabled(false));
        dispatch(setNextButtonToolTip('Please add finshes and colour reference to all parts.'));
      }
    } else {
      dispatch(setNextButtonEnabled(false));
      dispatch(setNextButtonToolTip('Please add finshes and colour reference to all parts.'));
    }
  }, [images, copyFrom, colourReference]);

  useEffect(() => {
    if (parts) {
      const _images = Object.keys(images)
        .filter((image) => images[image]?.length)
        .map((_item) => parseInt(_item));

      const _parts = parts
        ?.filter((_part) => !_images.includes(_part.actual_id))
        ?.map((_item) => _item.actual_id);

      const _copyFrom = Object.keys(copyFrom).filter((_item) => _parts.includes(copyFrom[_item]));

      const __copyFrom = copyFrom;
      const __value = value;

      _copyFrom.forEach((_item) => {
        delete __copyFrom[_item];
        delete __value[_item];
      });

      setCopyFrom(__copyFrom);
      setValue(__value);
      copyFromRef.current = __copyFrom;
    }
  }, [images]);

  const handleDrop = async (e, item) => {
    e.preventDefault();
    const files = await removeExifFromMultipleFiles(e.dataTransfer.files);
    let error = false
    for (const file of files) {
      if(error) return
      if (!checkFileTypes([file], ACCEPTABLE_IMAGE_TYPE_REQUEST, "Only images are allowed")) {
        error = true
      }
    }
    if (!error) {
      for (const file of files) {
        if (checkFileTypes([file], ACCEPTABLE_IMAGE_TYPE_REQUEST, "Only images are allowed")) {
           actualId = item?.actual_id || 0;
           handleFileChange([file], file.name, item?.actual_id || 0, true);
           setDefaultFlag(true);
        }
      }
    }
  };

  return (
    <Wrapper
      title={!isReadOnly && `Add default finishes of ${_requestSelector.requestName}`}
      className={className}
      isReadOnly={isReadOnly}>
      <div className="d-flex gap-3">
        <div className={`subMainWrapper ${isReadOnly && 'full-width'} position-relative`}>
          {loading ? (
            <>
              <div className="overlay"></div>
              <Loader loading={true} size={40} thickness={3} position="absolute" />
            </>
          ) : null}
          {parts?.length
            ? parts?.map((item) => (
                <div
                  key={item?.actual_id}
                  className={`fileWrapper ${
                    (images[item?.actual_id]?.length || copyFrom[item?.actual_id]) && 'bg-white'
                  }`}>
                  {/* Title on top if images were added or copy from other parts */}
                  {item?.actual_id
                    ? (images[item?.actual_id]?.length || copyFrom[item?.actual_id]) &&
                      (renamePart === item?.actual_id ? (
                        <input
                          value={rename}
                          onChange={handleRenameChange}
                          onBlur={() => handleRenameBlur(item.actual_id)}
                          onKeyDown={handleRenameKeyDown}
                          autoFocus={renamePart === item?.actual_id}
                        />
                      ) : (
                        <h5 className={isReadOnly && 'py-3'}>{item?.name}</h5>
                      ))
                    : null}
                  {item?.actual_id
                    ? (!images[item?.actual_id]?.length || copyFrom[item?.actual_id]) &&
                      parts?.length > 1 && (
                        <div
                          className={`copyFromWrapper ${
                            !Object.keys(images)?.length && 'disabled'
                          } ${copyFrom[item?.actual_id] && 'bg-white w-100'}`}
                          style={
                            isReadOnly && !copyFrom[item?.actual_id] ? { display: 'none' } : {}
                          }>
                          <span
                            className="d-flex align-items-center"
                            style={{ color: 'var(--primary-color)', fontWeight: '500' }}>
                            {copyFrom[item?.actual_id] ? 'Has same finish as' : 'Copy from'}
                          </span>

                          <StyledSelect
                            value={value[item?.actual_id] || { id: 0, label: 'Select a part' }}
                            onChange={(e) => handleChange(e, item?.actual_id)}
                            options={[
                              { id: 0, label: 'Select a part' },
                              ...parts.filter(
                                (part) =>
                                  images[part?.actual_id]?.length &&
                                  part.actual_id !== item.actual_id
                              ),
                            ]}
                            placeholder="Pick a part"
                            className="dropDown"
                          />
                        </div>
                      )
                    : null}
                  {!copyFrom[item?.actual_id] && (
                    <>
                      <UploadFile
                        id={`${item?.id}_inputUpload`}
                        itemID={item?.actual_id}
                        title={
                          item?.newPart ? (
                            <input
                              type="text"
                              className="partName"
                              placeholder={item?.name}
                              onClick={handleEnterPartName}
                              onKeyDown={(e) => handlePartNameKeyDown(item?.id, e)}
                              onBlur={(e) => handleEnterPartNameBlur(item?.id, e)}
                            />
                          ) : (
                            item?.name
                          )
                        }
                        className={cx('uploadFileWrapper', isReadOnly && 'disabled')}
                        subClassName="uploadFileSubWrapper"
                        subtitle={!isReadOnly && 'Drag photos here'}
                        uploadText={isReadOnly && 'No images added'}
                        acceptMutipleFiles={true}
                        onFileChange={handleFileChange}
                        hidden={images[item?.actual_id]?.length}
                        isDefault={item.isDefault}
                      />
                      {/* If Images were added to parts this part will be rendered */}
                      {images[item?.actual_id]?.length ? (
                        <>
                          <div>
                            <div className="imageWrapper">
                              {!isReadOnly && !item.isDefault ? (
                                <div
                                  className="threeDotIcon"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    setShowPartMenu(item?.actual_id);
                                  }}>
                                  <RequestThreeDotIcon />
                                </div>
                              ) : null}

                              <div
                                className={cx(
                                  'menu-wrapper',
                                  showPartMenu === item?.actual_id && 'show'
                                )}>
                                <div
                                  className="item"
                                  onClick={(e) => handleRename(e, item?.actual_id, item.value)}>
                                  Rename
                                </div>
                                {/* <div className="item" onClick={handleDuplicate}>
                                Duplicate
                              </div> */}
                                {/* <div
                                  className="item"
                                  onClick={() => handlePartDelete(item?.actual_id)}>
                                  Delete
                                </div> */}
                              </div>

                              <div className="ps-3" style={isReadOnly ? { width: '32%' } : {}}>
                                <div
                                  className="uploadFileWrapper"
                                  onDrop={() => {
                                    if (imageURL) {
                                      changeColorReference(
                                        {
                                          value: imageURL?.image,
                                          oldColorCode: images[imageURL?.item?.actual_id].find(
                                            (item) => item?.color_code
                                          ),
                                        },
                                        item?.actual_id
                                      );
                                    }
                                  }}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setShowColorInput(item?.actual_id);
                                  }}
                                  style={
                                    colourReference[item?.actual_id] && {
                                      border: 0,
                                      height:
                                        checkIsHexColor(
                                          colourReference[item?.actual_id]?.value?.color_code
                                        ) && isReadOnly
                                          ? '258px'
                                          : '242px',
                                      marginTop: isReadOnly ? '-1rem' : '1rem',
                                      width: isReadOnly ? '100%' : '330px',
                                    }
                                  }>
                                  {!colourReference[item?.actual_id]?.value?.id ? (
                                    <div
                                      onDrop={(e) => handleDrop(e, item)}
                                      className={`uploadTitle ${isReadOnly && 'h-100'}`}>
                                      {showColorInput !== item?.actual_id ? (
                                        !isReadOnly ? (
                                          <>
                                            <span>Drag an image for colour reference</span>
                                            <span>or</span>
                                            <span>Click to Enter Hex code</span>
                                          </>
                                        ) : (
                                          <div className="d-flex align-item-center">
                                            No Colour Reference
                                          </div>
                                        )
                                      ) : (
                                        <input
                                          type="text"
                                          style={{ margin: '2rem' }}
                                          onBlur={(e) =>
                                            handleInputBlur(
                                              item?.actual_id,
                                              e.target.value,
                                              item?.isDefault
                                            )
                                          }
                                          onKeyDown={handleKeyDown}
                                        />
                                      )}
                                    </div>
                                  ) : (
                                    <>
                                      <div
                                        className="colorReference"
                                        style={
                                          isReadOnly
                                            ? {
                                                zIndex: '1',
                                                width: '100%',
                                                bottom: checkIsHexColor(
                                                  colourReference[item?.actual_id]?.value
                                                    ?.color_code
                                                )
                                                  ? '-0.5rem'
                                                  : '-1.5rem',
                                              }
                                            : {}
                                        }>
                                        Colour Reference
                                      </div>
                                      {!isReadOnly ? (
                                        <div
                                          className="threeDotIcon"
                                          onClick={(e) => {
                                            setShowOption({ [item?.actual_id]: 0 });
                                            e.stopPropagation();
                                          }}>
                                          <RequestThreeDotIcon />
                                        </div>
                                      ) : null}
                                      {showOption && showOption[item?.actual_id] === 0 && (
                                        <div className="colorReferenceOptions">
                                          {showColorInput !== item?.actual_id ? (
                                            <div
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                setShowColorInput(item?.actual_id);
                                              }}>
                                              Enter Hex code
                                            </div>
                                          ) : (
                                            <input
                                              type="text"
                                              onBlur={(e) =>
                                                handleInputBlur(
                                                  item?.actual_id,
                                                  e.target.value,
                                                  item?.isDefault
                                                )
                                              }
                                              onKeyDown={handleKeyDown}
                                            />
                                          )}
                                          <div
                                            onClick={(e) =>
                                              handleColourReferenceRemove(e, item?.actual_id)
                                            }>
                                            Remove
                                          </div>
                                        </div>
                                      )}
                                      {!checkIsHexColor(
                                        colourReference[item?.actual_id]?.value?.color_code
                                      ) ? (
                                        !isReadOnly ? (
                                          <img
                                            src={colourReference[item?.actual_id]?.value?.url}
                                            style={{
                                              paddingTop: 0,
                                              paddingBottom: 0,
                                              border: '4px solid var(--primary-color)',
                                            }}
                                          />
                                        ) : (
                                          <UploadedFiles
                                            // key={index}
                                            className={'finishReadOnly'}
                                            _file={colourReference[item?.actual_id]?.value}
                                            onDeleteClick={() => {}}
                                            variant={2}
                                            isReadOnly={isReadOnly}
                                          />
                                        )
                                      ) : (
                                        <ElipsisTooltip
                                          title={
                                            colourReference[item?.actual_id].value.color_code || ''
                                          }
                                          hideoverlay
                                          alwaysShow={true}>
                                          {(props) => (
                                            <div
                                              {...props}
                                              className="hexColor"
                                              onClick={() => {
                                                navigator.clipboard.writeText(
                                                  colourReference[item?.actual_id].value.color_code
                                                );
                                                successToast(
                                                  `Copied hex color code ${
                                                    colourReference[item?.actual_id].value
                                                      .color_code
                                                  } to clipbaord`
                                                );
                                              }}
                                              style={{
                                                background:
                                                  colourReference[item?.actual_id].value.color_code,
                                                pointerEvents: 'all',
                                                width: isReadOnly ? '100%' : '330px',
                                              }}></div>
                                          )}
                                        </ElipsisTooltip>
                                      )}
                                    </>
                                  )}
                                </div>
                              </div>
                              {images[item?.actual_id].map((image, _index) => {
                                if (
                                  image?.image &&
                                  colourReference[[item?.actual_id]]?.value?.id !== image?.id
                                )
                                  return (
                                    <div
                                      className="image ms-3"
                                      style={isReadOnly ? { width: '32%' } : {}}
                                      onDrag={() => (imageURL = { image: image, item: item })}
                                      key={_index + 1}>
                                      {!isReadOnly ? (
                                        <div
                                          className="threeDotIcon"
                                          onClick={(e) => {
                                            setShowOption({ [item?.actual_id]: _index + 1 });
                                            e.stopPropagation();
                                          }}>
                                          <RequestThreeDotIcon />
                                        </div>
                                      ) : null}
                                      {showOption && showOption[item?.actual_id] === _index + 1 && (
                                        <div
                                          className="colorReferenceOptions"
                                          style={{ top: '85px' }}>
                                          <div
                                            onClick={(e) => {
                                              changeColorReference(
                                                {
                                                  value: image,
                                                  oldColorCode: images[item?.actual_id].find(
                                                    (item) => item?.color_code
                                                  ),
                                                },
                                                item?.actual_id
                                              );
                                              e.stopPropagation();
                                              setShowOption(null);
                                            }}>
                                            Set as color reference
                                          </div>
                                          <div
                                            onClick={() =>
                                              handleImageRemove(image.id, item?.actual_id)
                                            }>
                                            Delete
                                          </div>
                                        </div>
                                      )}
                                      {!isReadOnly ? (
                                        <img id={`_IMAGE_${image.id}`} src={image.url} draggable />
                                      ) : (
                                        <UploadedFiles
                                          // key={index}
                                          className={'finishReadOnly'}
                                          _file={image}
                                          onDeleteClick={() => {}}
                                          variant={2}
                                          isReadOnly={isReadOnly}
                                        />
                                      )}
                                    </div>
                                  );
                              })}
                            </div>
                          </div>

                          {!isReadOnly ? (
                            <label
                              className="uploadImages"
                              htmlFor={`file-upload ${item?.id}_inputUpload`}>
                              Upload more images
                            </label>
                          ) : null}
                        </>
                      ) : null}
                    </>
                  )}
                </div>
              ))
            : null}
        </div>
        {!isReadOnly && <Tips title="finishes" text={TIP_TEXT} marginTop={0} />}
      </div>
    </Wrapper>
  );
}

export default Finishes;
